import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import mapKeys from "lodash/mapKeys";

import { hydrate } from "@/lib";
import type { ProjectFinderParams } from "@/types";
import { fetchProjectFinder } from "./thunk";

// ToDo: will be removed when bazaar@next is deployed to production
const patternToBeRemoved = /q[[\]]|filters[[\]]|locale[[\]]|[[\]]+/g;
const parseQueryParams = (params: { [key: string]: any }) => {
  const obj = mapKeys(params, (value, key) => {
    if (patternToBeRemoved.test(key)) {
      return key.replace(patternToBeRemoved, "");
    }
    return key;
  });

  for (const i in obj) {
    obj[i] =
      obj[i] === "true" || obj[i] === "false" ? JSON.parse(obj[i]) : obj[i];
  }

  return obj;
};

export interface ProjectFinderState {
  error: string | null;
  status: "idle" | "fetching" | "saving";
  queryFilter: Partial<ProjectFinderParams>;
}

const initialState: ProjectFinderState = {
  status: "idle",
  error: null,
  queryFilter: {
    name: "",
    lang: "en",
    showSearch: true,
    showTagsFilter: true,
    showStateFilter: true,
    showYearFilter: true,
    showResetFilter: true,
    showProjectImage: true,
    showProjectName: true,
    showProjectNameTooltip: true,
    showProjectDescription: true,
    showProjectTags: true,
    showProjectTimestamp: true,
    enablePagination: true,
    lazyloadImages: true,
    projectLinkTarget: "_blank",
    timestampFormat: "DD MMM YYYY",
    maxRecords: 100,
    perPage: 6,
    projectDescLength: 214,
    stylesheetSrc: "",
    googleAnalyticsId: "",
    locales: {},
    filterByName: "",
    filterByState: "",
    filterByTag: "",
    filterByDate: "",
    componentSequence: ["cover", "timestamp", "title", "description", "tags"]
  }
};

export const projectFinderSlice = createSlice({
  name: "projectFinder",
  initialState,
  reducers: {
    // ToDo: will be removed when bazaar@next is deployed to production
    updateQueryFilter: (state, action) => {
      const qp = parseQueryParams(action.payload);
      const queryParams = {
        baseUrl: qp.baseUrl,
        showSearch: !qp.hideSearch,
        showTagsFilter: !qp.hideTagsFilter,
        showYearFilter: !qp.hideYearFilter,
        showStateFilter: !qp.hideStateFilter,
        showResetFilter: !qp.hideResetFilterButton,
        showProjectName: !qp.hideProjectName,
        showProjectNameTooltip: !qp.hideProjectNameTooltip,
        showProjectImage: !qp.hideProjectCover,
        showProjectDescription: !qp.hideProjectDescription,
        projectDescLength: qp.maxProjectDescLength || 214,
        showProjectTags: !qp.hideProjectTags,
        showProjectTimestamp: !qp.hideProjectTimestamp,
        timestampFormat: qp.timestampFormat || "DD MMM YYYY",
        perPage: qp.projectsPerPage || 6,
        maxRecords: qp.maxProjects || 100,
        lazyloadImages: !qp.imageLazyLoad,
        enablePagination: !!qp.enablePagination,
        timestampAttribute: qp.timestampDateType || "publishedAt",
        projectLinkTarget: qp.projectLinkTarget || "_blank",
        componentSequence: qp.componentSequence || [
          "cover",
          "timestamp",
          "title",
          "description",
          "tags"
        ],
        stylesheetSrc: qp.stylesheetSrc || "",
        filterByName: qp.name || "",
        filterByState: qp.state || "",
        filterByTag: qp.platformAnalyticsTagList || "",
        filterByDate: qp.publishedAt || "",
        locales: {
          "projects.resetBtn": qp["projects.resetBtn"] || "",
          "projects.tags.all": qp["projects.tags.all"] || "",
          "projects.noContent": qp["projects.noContent"] || "",
          "projects.states.all": qp["projects.states.all"] || "",
          "projects.timeStamp.all": qp["projects.timeStamp.all"] || "",
          "projects.timeStampText": qp["projects.timeStampText"] || "",
          "projects.states.archived": qp["projects.states.archived"] || "",
          "projects.states.published": qp["projects.states.published"] || "",
          "projects.search.placeholder": qp["projects.search.placeholder"] || ""
        }
      };
      state.queryFilter = { ...state.queryFilter, ...queryParams };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => ({
        ...state,
        ...action.payload[projectFinderSlice.name]
      }))
      .addCase(fetchProjectFinder.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchProjectFinder.fulfilled, (state, action) => {
        state.status = "idle";
        state.queryFilter = { ...state.queryFilter, ...action.payload };
      })
      .addCase(fetchProjectFinder.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      });
  }
});

export const { updateQueryFilter } = projectFinderSlice.actions;
